import React, { useRef, useState } from 'react';
import { cn } from 'lib/utils';

export interface FlatBar {
  key: string;
  value: number;
  /** Tooltip heading, e.g. the day or the score band. */
  label: string;
  /** Tooltip figure, e.g. "142 ta topshiriq". */
  valueLabel: string;
  /** Light red for bars that sit on the failing side of a line. */
  tone?: 'danger';
}

interface FlatBarChartProps {
  bars: FlatBar[];
  /** Drawn in ink while no bar is hovered or focused (e.g. the busiest day). */
  highlightKey?: string;
  height: number;
  /** Space between bars in px. */
  gap?: number;
  'aria-label': string;
  className?: string;
  /** Rendered over the plot area (e.g. a threshold line). */
  children?: React.ReactNode;
}

const MIN_BAR_PX = 3;

/**
 * Flat columns on a baseline: quiet grey bars, the highlighted one in ink, a dark
 * tooltip over the bar under the pointer or keyboard focus. One tab stop; arrow
 * keys, Home and End walk the bars.
 */
export function FlatBarChart({
  bars,
  highlightKey,
  height,
  gap = 3,
  'aria-label': ariaLabel,
  className,
  children,
}: FlatBarChartProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const max = bars.reduce((m, bar) => Math.max(m, bar.value), 0);
  const inkKey = activeKey ?? highlightKey;

  const moveFocus = (event: React.KeyboardEvent, index: number) => {
    const last = bars.length - 1;
    const target =
      event.key === 'ArrowRight'
        ? Math.min(last, index + 1)
        : event.key === 'ArrowLeft'
          ? Math.max(0, index - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : -1;
    if (target < 0) return;
    event.preventDefault();
    setFocusIndex(target);
    barRefs.current[target]?.focus();
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('relative flex items-end border-b border-border', className)}
      style={{ height, gap }}
    >
      {bars.map((bar, index) => {
        const ink = bar.key === inkKey;
        const barHeight = max > 0 ? Math.max(MIN_BAR_PX, Math.round((bar.value / max) * height)) : MIN_BAR_PX;
        // Keep the tooltip inside the chart: edge bars anchor it to their own side.
        const position = bars.length > 2 ? index / (bars.length - 1) : 0.5;
        const anchor = position < 0.25 ? 'left-0' : position > 0.75 ? 'right-0' : 'left-1/2 -translate-x-1/2';
        return (
          <span
            key={bar.key}
            ref={(el) => {
              barRefs.current[index] = el;
            }}
            role="img"
            tabIndex={index === focusIndex ? 0 : -1}
            aria-label={`${bar.label}: ${bar.valueLabel}`}
            data-flat-bar={bar.key}
            data-ink={ink || undefined}
            onMouseEnter={() => setActiveKey(bar.key)}
            onMouseLeave={() => setActiveKey(null)}
            onFocus={() => {
              setFocusIndex(index);
              setActiveKey(bar.key);
            }}
            onBlur={() => setActiveKey(null)}
            onKeyDown={(event) => moveFocus(event, index)}
            className="relative flex h-full min-w-0 flex-1 items-end rounded-t-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              aria-hidden
              className={cn(
                'block w-full rounded-t-[2px] transition-colors duration-150',
                ink ? 'bg-chart-1' : bar.tone === 'danger' ? 'bg-error-300' : 'bg-chart-2',
              )}
              style={{ height: barHeight }}
            />
            {activeKey === bar.key && (
              <span
                aria-hidden
                data-flat-bar-tooltip
                className={cn(
                  'pointer-events-none absolute z-10 flex flex-col gap-px whitespace-nowrap rounded-md bg-foreground px-2 py-1.5 text-[11.5px] leading-tight text-background',
                  anchor,
                )}
                style={{ bottom: barHeight + 6 }}
              >
                <span className="text-background/70">{bar.label}</span>
                <span className="font-semibold tabular-nums">{bar.valueLabel}</span>
              </span>
            )}
          </span>
        );
      })}
      {children}
    </div>
  );
}

export default FlatBarChart;
